import React from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import DateNavigation from './DateNavigation';
import ModelRow from './ModelRow';
import { DateInfo } from './types';
import { ModelState } from '../../contexts/types';

interface ModelsContentProps {
  currentDateIndex: number;
  onPrevious: () => void;
  onNext: () => void;
  isAtFirstDate: boolean;
  isAtLastDate: boolean;
  currentDates: DateInfo[];
  models: ModelState[];
  onSelectionChange: (modelId: string, runs: { date: string; time: string }[]) => void;
  onModelToggle: (modelId: string) => void;
  loading: boolean;
  error: string | null;
}

const ModelsContent: React.FC<ModelsContentProps> = ({
  currentDateIndex,
  onPrevious,
  onNext,
  isAtFirstDate,
  isAtLastDate,
  currentDates,
  models,
  onSelectionChange,
  onModelToggle,
  loading,
  error,
}) => {
  return (
    <div className="models-content-container flex-1 min-h-0 rounded-lg mx-2 mb-2 flex flex-col relative">
      {/* Date Navigation */}
      <div className="flex-shrink-0">
        <DateNavigation
          currentDateIndex={currentDateIndex}
          onPrevious={onPrevious}
          onNext={onNext}
          isAtFirstDate={isAtFirstDate}
          isAtLastDate={isAtLastDate}
          currentDates={currentDates}
        />
      </div>

      {/* Selected Runs Summary */}
      {!loading && !error && (
        <div className="px-2 pt-2 pb-2">
          <div className="flex flex-wrap gap-2">
            {models.flatMap((m) =>
              (m.isSelected ? m.selectedRuns : []).map((run, idx) => {
                return (
                  <div
                    key={`${m.id}-${idx}-${run.date}-${run.time}`}
                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] border bg-gray-50 border-gray-200 text-gray-700`}
                    title={`${m.label} • ${run.date} ${run.time}:00`}
                  >
                    <span className="font-medium truncate max-w-[120px]">{m.label}</span>
                    <span className="opacity-70">
                      {run.date} {run.time}:00
                    </span>
                    <button
                      onClick={() => {
                        const next = m.selectedRuns.filter(
                          (r) => !(r.date === run.date && r.time === run.time),
                        );
                        onSelectionChange(m.id, next);
                        if (next.length === 0 && m.isSelected) {
                          onModelToggle(m.id);
                        }
                      }}
                      className="ml-1 px-1 rounded hover:bg-black/5"
                    >
                      ×
                    </button>
                  </div>
                );
              }),
            )}
          </div>
        </div>
      )}

      {/* Models List - Scrollable */}
      <div
        className="flex-1 min-h-0 overflow-y-auto overscroll-contain"
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
      >
        {loading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-6 w-6 animate-spin text-gray-500 dark:text-gray-400" />
            <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Loading models...</span>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center p-8">
            <AlertCircle className="h-6 w-6 text-red-500" />
            <span className="ml-2 text-sm text-red-600 dark:text-red-400">{error}</span>
          </div>
        ) : models.length === 0 ? (
          <div className="flex items-center justify-center p-8">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              No models available for this configuration
            </span>
          </div>
        ) : (
          models.map((model) => (
            <ModelRow
              key={model.id}
              modelId={model.id}
              name={model.label}
              isSelected={model.isSelected}
              selectedRuns={model.selectedRuns}
              currentDates={currentDates}
              onSelectionChange={onSelectionChange}
              onModelToggle={onModelToggle}
              disabled={!model.isAvailable}
              latestRun={model.latestRun}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default ModelsContent;
